/*
 * Description:
 * ---------------------
 * The AI analyst on the Firewall screen's Overview: what the box's behaviour
 * model noted its devices doing that their own history does not hold
 * (cloud-anchor/pkg/firewall behaviour.go, /firewall/findings), each as a
 * sentence with its evidence and what to do: ask Ada to explain it, block the
 * place it was about, or put it away. Beside it, Ada on demand: a briefing on
 * the box's firewall, and rules written from a sentence the operator types,
 * which she proposes and the operator confirms. Wording in firewallWords.js.
 *
 * ! file components/FirewallAnalyst.js
 */
import React, { useState } from "react";
import { FiArrowRight, FiX } from "react-icons/fi";
import { TbBan } from "react-icons/tb";
import { FEATURE_PRESENT } from "../hooks/useEdgeFeature";
import useEdgeWrite from "../hooks/useEdgeWrite";
import { AiBadge, AiIcon } from "./AiIcon";
import { askAda } from "../utils/askAda";
import { deviceName, kindIcon } from "./FirewallDevices";
import * as W from "./firewallWords";

function Finding({ f, rows, busy, onAsk, onBlock, onDismiss }) {
  const name = deviceName(rows, f.device);
  const w = W.noteWords(f, name);
  const Kind = kindIcon(name || "");
  const at = new Date(f.at);
  return (
    <li className={`fw-finding-row is-${f.severity}`}>
      <span className="fw-finding-dev" aria-hidden="true"><Kind size={18} strokeWidth={1.8} /></span>
      <div className="fw-finding-main">
        <strong>{w.title}</strong>
        <p>{w.detail}</p>
        <small>{Number.isNaN(at.getTime()) ? "" : at.toLocaleString([], { dateStyle: "medium", timeStyle: "short", hourCycle: "h23" })}</small>
      </div>
      <div className="fw-finding-acts">
        <button type="button" className="fw-btn is-ai" onClick={onAsk}><AiIcon size={15} aria-hidden="true" /> {W.ANALYST.explain}</button>
        {f.place && !f.stopped && (
          <button type="button" className="fw-btn" disabled={busy} onClick={onBlock}><TbBan size={14} aria-hidden="true" /> {W.ANALYST.block}</button>
        )}
        <button type="button" className="fw-icon-btn" aria-label={W.ANALYST.dismiss} disabled={busy} onClick={onDismiss}><FiX size={16} /></button>
      </div>
    </li>
  );
}

export default function FirewallAnalyst({ selectedNode, findings, rows, locked, onBlockPlace }) {
  const write = useEdgeWrite(selectedNode, findings.refresh);
  const [describe, setDescribe] = useState("");
  const view = findings.state === FEATURE_PRESENT && findings.payload?.available ? findings.payload : null;
  const list = view?.findings || [];
  const brief = () => askAda(selectedNode, W.ANALYST.briefQuestion, { send: true });
  const submitDescribe = (e) => {
    e.preventDefault();
    if (describe.trim()) askAda(selectedNode, W.ANALYST.describeQuestion(describe.trim()), { send: true });
  };
  return (
    <section className="fw-card fw-analyst">
      <header className="fw-card-head is-split">
        <div>
          <h3 className="fw-card-title"><AiBadge /> {W.ANALYST.title}</h3>
          <p>{W.ANALYST.sub}</p>
          {view && <p className="fw-muted">{W.ANALYST.learning(view.devices, view.learnt)}</p>}
        </div>
        <button type="button" className="fw-btn is-ada" onClick={brief}>{W.ANALYST.brief} <FiArrowRight size={14} aria-hidden="true" /></button>
      </header>
      {write.error && <div className="fw-notice is-bad" role="alert">{write.error}</div>}
      {!view && <p className="fw-muted">{W.ANALYST.unavailable}</p>}
      {view && list.length === 0 && <p className="fw-analyst-quiet">{W.ANALYST.quiet}</p>}
      {list.length > 0 && (
        <ul className="fw-findings">
          {list.slice(0, 8).map((f) => (
            <Finding key={f.id} f={f} rows={rows} busy={write.busy || locked}
              onAsk={() => askAda(selectedNode, W.noteQuestion(f, deviceName(rows, f.device)))}
              onBlock={() => onBlockPlace(f)}
              onDismiss={() => write.send("POST", "/firewall/findings/dismiss", { id: f.id })} />
          ))}
        </ul>
      )}
      <form className="fw-describe" onSubmit={submitDescribe}>
        <label htmlFor="fw-describe">{W.ANALYST.describe}</label>
        <div className="fw-describe-row">
          <input id="fw-describe" value={describe} onChange={(e) => setDescribe(e.target.value)} placeholder={W.ANALYST.describeHint} />
          <button type="submit" className="fw-btn is-primary" disabled={!describe.trim()}><AiIcon size={15} aria-hidden="true" /> {W.ANALYST.describeGo}</button>
        </div>
      </form>
    </section>
  );
}
